import { ScrollArea } from '@base-ui/react/scroll-area';
import { GitBranch, Globe, History, RotateCcw } from 'lucide-react';
import { Suspense, useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { GitPanelSkeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast-store';
import { Tooltip } from '@/components/ui/tooltip';
import { createApiClient } from '@/lib/api-client';
import { useStore } from '@/lib/store';
import { cn } from '@/lib/utils';

import { GitBranchDialog } from './git-branch-dialog';
import { GitBranchSelector } from './git-branch-selector';
import { GitCloneDialog } from './git-clone-dialog';
import { GitCommitForm } from './git-commit-form';
import { GitHistoryPanel } from './git-history-panel';
import { GitStatusList } from './git-status-list';
import { useGitBranches } from '../hooks/use-git-branches';
import { useGitLog } from '../hooks/use-git-log';
import { useGitMutations } from '../hooks/use-git-mutations';
import { useGitStatusSuspense } from '../hooks/use-git-status';
import { groupStatusEntries } from '../lib/status-helpers';

interface GitPanelProperties {
	projectId: string;
	className?: string;
}

/**
 * GitPanel with Suspense boundary.
 * Shows a skeleton while loading the initial git status.
 */
export function GitPanel({ projectId, className }: GitPanelProperties) {
	return (
		<Suspense fallback={<GitPanelSkeleton />}>
			<GitPanelContent projectId={projectId} className={className} />
		</Suspense>
	);
}
function GitPanelContent({ projectId, className }: GitPanelProperties) {
	const [showHistory, setShowHistory] = useState(false);
	const [branchDialogOpen, setBranchDialogOpen] = useState(false);
	const [discardAllConfirmOpen, setDiscardAllConfirmOpen] = useState(false);
	const [discardFilePath, setDiscardFilePath] = useState<string | undefined>();
	const [cloneDialogOpen, setCloneDialogOpen] = useState(false);

	const openFile = useStore((state) => state.openFile);
	const showGitDiff = useStore((state) => state.showGitDiff);
	const apiClient = useMemo(() => createApiClient(projectId), [projectId]);

	// Fetch diff from API and show it in the editor
	const handleFileClick = useCallback(
		async (path: string) => {
			const normalizedPath = path.startsWith('/') ? path : `/${path}`;
			// Strip leading slash for git paths (git uses relative paths)
			const gitPath = path.startsWith('/') ? path.slice(1) : path;

			try {
				const response = await apiClient.git.diff.$get({ query: { path: gitPath } });
				if (!response.ok) {
					// Fallback: just open the file without diff
					openFile(normalizedPath);
					return;
				}

				const data = await response.json();
				const diff = data.diff;
				if (diff?.beforeContent !== undefined && diff?.afterContent !== undefined) {
					showGitDiff({
						path: normalizedPath,
						beforeContent: diff.beforeContent,
						afterContent: diff.afterContent,
					});
				} else {
					// No content available (shouldn't happen), fall back to opening file
					openFile(normalizedPath);
				}
			} catch {
				// On error, just open the file without diff
				toast.error('Failed to load diff');
				openFile(normalizedPath);
			}
		},
		[apiClient, openFile, showGitDiff],
	);

	// Fetch diff for a file at a specific commit and show it in the editor
	const handleCommitFileClick = useCallback(
		async (path: string, objectId: string) => {
			const normalizedPath = path.startsWith('/') ? path : `/${path}`;
			const gitPath = path.startsWith('/') ? path.slice(1) : path;

			try {
				const response = await apiClient.git.diff.file.$get({ query: { objectId, path: gitPath } });
				if (!response.ok) {
					openFile(normalizedPath);
					return;
				}

				const data = await response.json();
				const diff = data.diff;
				if (diff?.beforeContent !== undefined && diff?.afterContent !== undefined) {
					showGitDiff({
						path: normalizedPath,
						beforeContent: diff.beforeContent,
						afterContent: diff.afterContent,
						description: objectId.slice(0, 7),
					});
				} else {
					openFile(normalizedPath);
				}
			} catch {
				toast.error('Failed to load commit diff');
				openFile(normalizedPath);
			}
		},
		[apiClient, openFile, showGitDiff],
	);

	// Data hooks
	const { entries, initialized } = useGitStatusSuspense({ projectId });
	const { branches, currentBranch } = useGitBranches({ projectId, enabled: initialized });
	const { commits, isLoading: isLogLoading } = useGitLog({ projectId, enabled: initialized && showHistory });

	// Mutations
	const mutations = useGitMutations({ projectId });

	// Derived
	const groups = groupStatusEntries(entries);
	const hasStagedChanges = groups.staged.length > 0;
	const changedFileCount = groups.unstaged.length + groups.untracked.length;

	// Not initialized
	if (!initialized) {
		return (
			<div className={cn('flex h-full flex-col items-center justify-center gap-3 px-4', className)}>
				<GitBranch className="size-8 text-text-secondary" />
				<p className="text-center text-sm text-text-secondary">Git not initialized</p>
				<Button variant="default" size="sm" isLoading={mutations.isInitPending} onClick={() => mutations.initializeRepository()}>
					Initialize Repository
				</Button>
			</div>
		);
	}

	return (
		<div className={cn('flex h-full flex-col', className)}>
			<div className="flex items-center justify-between px-3 pt-1.5 pb-0.5">
				<span
					className="
						text-xs font-semibold tracking-wider text-text-secondary uppercase
					"
				>
					Git
				</span>
				<div className="flex items-center gap-0.5">
					<Tooltip content="Git remote">
						<button
							type="button"
							onClick={() => setCloneDialogOpen(true)}
							className={cn(
								`
									flex size-6 cursor-pointer items-center justify-center rounded-sm
									text-text-secondary
								`,
								`
									transition-colors
									hover:bg-bg-tertiary hover:text-text-primary
								`,
							)}
						>
							<Globe className="size-3.5" />
						</button>
					</Tooltip>
					<Tooltip content={showHistory ? 'Show changes' : 'Show history'}>
						<button
							type="button"
							onClick={() => setShowHistory(!showHistory)}
							className={cn(
								`
									flex size-6 cursor-pointer items-center justify-center rounded-sm
									text-text-secondary
								`,
								`
									transition-colors
									hover:bg-bg-tertiary hover:text-text-primary
								`,
								showHistory && 'text-accent',
							)}
						>
							<History className="size-3.5" />
						</button>
					</Tooltip>
					<Tooltip content="Discard all changes">
						<button
							type="button"
							onClick={() => setDiscardAllConfirmOpen(true)}
							disabled={mutations.isDiscardPending}
							className={cn(
								`
									flex size-6 cursor-pointer items-center justify-center rounded-sm
									text-text-secondary
								`,
								`
									transition-colors
									hover:bg-bg-tertiary hover:text-error
								`,
								mutations.isDiscardPending && 'cursor-not-allowed opacity-50',
							)}
						>
							<RotateCcw className="size-3.5" />
						</button>
					</Tooltip>
				</div>
			</div>

			<div className="px-2 pb-1">
				<GitBranchSelector
					branches={branches}
					currentBranch={currentBranch}
					onCheckout={(reference) => mutations.checkout(reference)}
					onCreateBranch={() => setBranchDialogOpen(true)}
					onMerge={(branch) => mutations.merge(branch)}
					onDeleteBranch={(name) => mutations.deleteBranch(name)}
					disabled={mutations.isBranchPending || mutations.isMergePending}
					hasChanges={changedFileCount > 0 || hasStagedChanges}
				/>
			</div>

			<ScrollArea.Root className="h-full flex-1 overflow-hidden">
				<ScrollArea.Viewport className="size-full">
					{showHistory ? (
						<GitHistoryPanel
							projectId={projectId}
							commits={commits}
							isLoading={isLogLoading}
							branches={branches}
							onCheckout={(reference) => mutations.checkout(reference)}
							onFileClick={(path, objectId) => void handleCommitFileClick(path, objectId)}
							isCheckoutPending={mutations.isBranchPending}
						/>
					) : (
						<>
							<GitCommitForm
								onCommit={(message) => mutations.commit({ message })}
								isCommitting={mutations.isCommitPending}
								isCommitSuccess={mutations.isCommitSuccess}
								hasStagedChanges={hasStagedChanges}
								error={mutations.commitError ?? undefined}
							/>

							<GitStatusList
								entries={entries}
								onStage={(paths) => mutations.stageFiles(paths)}
								onUnstage={(paths) => mutations.unstageFiles(paths)}
								onDiscard={(path) => setDiscardFilePath(path)}
								onFileClick={(path) => void handleFileClick(path)}
							/>
						</>
					)}
				</ScrollArea.Viewport>
				<ScrollArea.Scrollbar className="flex w-2 touch-none bg-transparent p-0.5 select-none" orientation="vertical">
					<ScrollArea.Thumb className="relative flex-1 rounded-full bg-border" />
				</ScrollArea.Scrollbar>
			</ScrollArea.Root>

			<GitCloneDialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen} projectId={projectId} />

			<GitBranchDialog
				open={branchDialogOpen}
				onOpenChange={setBranchDialogOpen}
				onCreateBranch={(name, checkout) => {
					mutations.createBranch({ name, checkout });
					setBranchDialogOpen(false);
				}}
				isPending={mutations.isBranchPending}
			/>

			<ConfirmDialog
				open={discardAllConfirmOpen}
				onOpenChange={setDiscardAllConfirmOpen}
				title="Discard All Changes"
				description={
					<>
						Are you sure you want to discard all changes in{' '}
						<strong>
							{changedFileCount} {changedFileCount === 1 ? 'file' : 'files'}
						</strong>
						? This will revert all modifications to the last committed state. This action cannot be undone.
					</>
				}
				confirmLabel="Discard All"
				variant="danger"
				onConfirm={() => {
					mutations.discardAll();
					setDiscardAllConfirmOpen(false);
				}}
			/>

			<ConfirmDialog
				open={discardFilePath !== undefined}
				onOpenChange={(open) => {
					if (!open) setDiscardFilePath(undefined);
				}}
				title="Discard Changes"
				description={
					<>
						Are you sure you want to discard changes in <strong>{discardFilePath}</strong>? This will revert the file to its last committed
						state. This action cannot be undone.
					</>
				}
				confirmLabel="Discard Changes"
				variant="danger"
				onConfirm={() => {
					if (discardFilePath) {
						mutations.discardChanges(discardFilePath);
					}
					setDiscardFilePath(undefined);
				}}
			/>
		</div>
	);
}
